'use client';
import { useEffect, useRef, useState } from 'react';
import { useSession, signOut } from 'next-auth/react';

/**
 * Automatinis atsijungimas po neveiklumo — TIK administracijai (ADMIN/DIRECTOR),
 * kaip banke/Sodroje/VMI. Operatoriai ir montuotojai (WORKER/MECHANIC) NELIEČIAMI —
 * jie dirba telefonuose ir turi likti prisijungę (kitaip vėl reikėtų vesti kodą).
 *
 * Po 25 min neveiklumo parodomas 60 s įspėjimas; nesureagavus — atsijungiama.
 */
const IDLE_MS = 25 * 60 * 1000; // 25 min neveiklumo
const WARN_MS = 60 * 1000; // 60 s įspėjimas prieš atsijungiant

export default function IdleLogout() {
  const { data: session } = useSession();
  const role = (session?.user as { role?: string } | undefined)?.role;
  const isStaff = role === 'ADMIN' || role === 'DIRECTOR';
  const [warning, setWarning] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isStaff) return; // tik administracijai

    const clear = () => {
      if (timer.current) clearTimeout(timer.current);
      if (warnTimer.current) clearTimeout(warnTimer.current);
    };
    const reset = () => {
      clear();
      setWarning(false);
      timer.current = setTimeout(() => {
        setWarning(true);
        warnTimer.current = setTimeout(() => {
          void signOut({ callbackUrl: '/prisijungimas' });
        }, WARN_MS);
      }, IDLE_MS);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    // Veiklos metu atidedam laikmatį (throttle — ne kiekvieno įvykio).
    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last < 2000) return; // throttle 2 s
      last = now;
      if (!warning) reset(); // jei jau rodomas įspėjimas — laukiam mygtuko
    };
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    reset();

    return () => {
      clear();
      events.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [isStaff, warning]);

  if (!isStaff || !warning) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto overscroll-contain bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-slate-900">Ar tebedirbate?</h2>
        <p className="mt-1 text-sm text-slate-600">
          Dėl saugumo po neveiklumo būsite automatiškai atjungti. Paspauskite „Likti prisijungus".
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() => void signOut({ callbackUrl: '/prisijungimas' })}
            className="rounded-lg border px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
          >
            Atsijungti
          </button>
          <button
            onClick={() => setWarning(false)}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Likti prisijungus
          </button>
        </div>
      </div>
    </div>
  );
}
